import * as React from "react";
import { toastr } from "react-redux-toastr";
import api from "../../api";
import RemoteDataDropDown from "../../components/dropdown/RemoteDataDropDown";
import { ContainerLoader } from "../../components/loader";
import { City, ILeadBulkAssign, useLead } from "../../core";
import "../../styles/bulk-action.scss";
import { AvatarImage } from "../../components/render-file/AvatarImage";
import { decodeHtmlEntities } from "../../utils/common";

interface IBulkAction {
  leadIds: string[];
  onClose: (shouldReload: boolean) => void;
}

export const BulkAction: React.FunctionComponent<IBulkAction> = (props) => {
  const { updating, bulkAssign } = useLead(api.lead);
  const [selectedCityId, setSelectedCityId] = React.useState<any>();
  const [crmAgent, setCRMAgent] = React.useState();
  const [salesPerson, setSalesPerson] = React.useState();
  const [reasonText, setReason] = React.useState("");
  const [error, setError] = React.useState({city: "", assignee: ""});

  const loadAllCities = async () => {
    const cityData = await api.AddNewRequirement.loadAllCities();
    const cityOptions = cityData?.map((item: City) => ({
      value: item._id,
      name: decodeHtmlEntities(item?.cityName),
    }));
    cityOptions.sort((a, b) => a.name.localeCompare(b.name));
    return cityOptions;
  };

  const loadSalesPersons = async () => {
    if (selectedCityId) {
      return api.customer.searchSalesPerson(
        { hideBlocked: true, cityIds: [selectedCityId] },
        "lead-assignees"
      );
    }
    return [];
  };

  const loadCrmAgents = async () => {
    if (selectedCityId) {
      return api.customer.searchCrmAgents(
        { hideBlocked: true, cityIds: [selectedCityId] },
        "lead-assignees",
      );
    };
    return [];
  };

  React.useEffect(() => {
    setReason("");
    setError({city: "", assignee: ""});
  }, []);

  React.useEffect(() => {
    setError({city: "", assignee: ""});
  }, [selectedCityId, crmAgent, salesPerson, reasonText]);

  React.useEffect(() => {
    setCRMAgent(undefined);
    setSalesPerson(undefined);
  }, [selectedCityId]);

  const onSave = () => {
    const errorDetails = {city: "", assignee: ""}

    if (crmAgent === undefined && salesPerson === undefined) {
      errorDetails.assignee = "Please select either Support Executive  or Transaction Manager";
    }
    if (selectedCityId === undefined) {
      errorDetails.city = "Please select a city"; 
    }

    if(errorDetails.city !== "" || errorDetails.assignee !== "") {
      setError(errorDetails);
      return;
    }
    const data: ILeadBulkAssign = {
      leadIds: props.leadIds,
      cityId: selectedCityId,
      crmAgentId: crmAgent,
      salesPersonId: salesPerson,
      reason: reasonText,
    };
    bulkAssign(data, () => {
      toastr.success(
        "Success",
        `Successfully assigned values for ${props.leadIds.length} lead(s)`
      );
      props.onClose(true);
    });
  };

  const imageRenderer = ({ item }: any) => {
    return (
      <div className="custom-dropdown__menu-item border-bottom d-flex align-items-center cursor-pointer p-1">
        <div className="avatar-progress avatar-progress--xs position-relative cursor-pointer mr-2">
          <svg>
            <circle r="12" cx="50%" cy="50%" className="default"></circle>
            <circle
              r="12"
              cx="50%"
              cy="50%"
              className="progress"
              stroke-dasharray="117"
              stroke-dashoffset="75"
            ></circle>
          </svg>
          <AvatarImage
            image={item.avatar}
            className="position-absolute left-0 right-0 top-0 bottom-0 m-auto rounded-circle"
          ></AvatarImage>
        </div>
        <div className="font-normal font-weight-semibold">
          {item.knownAs || item.name}
        </div>
      </div>
    );
  };

  return (
    <div className="bulk-action-form border bg-white in">
      <div className="popup-header bg-gray-100 p-3 d-flex align-items-center border-bottom">
        <h5 className="mb-0">
          Bulk assignment of {props.leadIds.length} lead(s)
        </h5>
        <span className="cursor-pointer pl-2 ml-auto font-normal">
          <i
            onClick={() => {
              props.onClose(false);
            }}
            className="icon-close font-weight-bold"
          ></i>
        </span>
      </div>
      <div className="popup-body px-3 py-4 bulk-action-scroll">
        <div className="pb-2">
          <div className="form-group">
            <label className="font-sm text-uppercase font-weight-semibold">
              Choose City<span className="text-danger">*</span>
            </label>
            <RemoteDataDropDown
              loadData={loadAllCities}
              textField="name"
              value={selectedCityId}
              defaultValue={selectedCityId}
              valueField="value"
              onChange={(val) => {
                setSelectedCityId(val.value);
              }}
              placeholder="Select City"
            ></RemoteDataDropDown>
            <span className="error text-danger font-sm ">{error.city}</span>
          </div>
          <div className="form-group">
            <label className="font-sm text-uppercase font-weight-semibold">
              ASSIGNED TO SUPPORT EXECUTIVE
            </label>
            <RemoteDataDropDown
              key={"crmAgent" + selectedCityId}
              loadData={loadCrmAgents}
              textField="name"
              value={crmAgent}
              defaultValue={crmAgent}
              valueField="_id"
              onChange={(val) => {
                setCRMAgent(val._id);
              }}
              placeholder="Select Support Executive"
              itemComponent={imageRenderer}
            ></RemoteDataDropDown>
          </div>
          <div className="form-group">
            <label className="font-sm text-uppercase font-weight-semibold">
              ASSIGNED TO TRANSACTION MANAGER
            </label>
            <RemoteDataDropDown
              key={"salesPerson" + selectedCityId}
              loadData={loadSalesPersons}
              textField="name"
              value={salesPerson}
              defaultValue={salesPerson}
              valueField="_id"
              itemComponent={imageRenderer}
              onChange={(val) => {
                setSalesPerson(val._id);
              }}
              placeholder="Add Transaction Manager"
            ></RemoteDataDropDown>
            <span className="error text-danger font-sm ">{error.assignee}</span>
          </div>
          <div className="form-group mb-0">
            <label className="font-sm text-uppercase font-weight-semibold">
              REASON
            </label>
            <textarea
              value={reasonText}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              className="form-control rounded-sm font-weight-semibold h-auto"
              name=""
              id=""
              cols={30}
              rows={3}
            ></textarea>
          </div>
        </div>
      </div>
      {!updating ? (
        <div className="popup-footer bg-gray-100 px-3 py-2 border-top text-right">
          <div className="d-inline-block bg-white mr-1">
            <button
              onClick={() => {
                props.onClose(false);
              }}
              type="button"
              className="btn btn-outline-secondary font-weight-semibold font-normal px-4"
            >
              Cancel
            </button>
          </div>
          <div className="d-inline-block bg-white">
            <button
              onClick={() => {
                onSave();
              }}
              type="submit"
              className="btn btn-primary font-weight-semibold font-normal px-4"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <ContainerLoader></ContainerLoader>
      )}
    </div>
  );
};
